
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Star, Award, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useStarData } from '@/hooks/useStarData';

interface DailyHistory {
  date: string;
  starsEarned: number;
  items: Array<{
    title: string;
    stars: number;
    isTask: boolean;
  }>;
}

const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<DailyHistory[]>([]);
  const navigate = useNavigate();
  const { data: appData } = useStarData();

  useEffect(() => {
    if (!appData) {
      return;
    }
    
    const historyItems: Record<string, Array<{title: string, stars: number, isTask: boolean}>> = {};
    const starsEarnedByDay: Record<string, number> = {};
    
    // Helper function to normalize date strings to YYYY-MM-DD format
    const normalizeDate = (dateString: string): string => {
      if (!dateString) return '';
      
      // Extract just the date part if ISO format
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      
      // Ensure consistent format for all dates
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error(`Error normalizing date ${dateString}:`, error);
        return '';
      }
    };
    
    // Process tasks
    appData.tasks.forEach(task => {
      if (task.lastCompleted) {
        const completionDate = normalizeDate(task.lastCompleted);
        if (completionDate && completionDate.length > 0) {
          if (!historyItems[completionDate]) {
            historyItems[completionDate] = [];
            starsEarnedByDay[completionDate] = 0;
          }
          
          historyItems[completionDate].push({
            title: task.title,
            stars: task.stars,
            isTask: true
          });
          
          starsEarnedByDay[completionDate] += task.stars;
        }
      }
    });
    
    // Process rewards
    appData.rewards.forEach(reward => {
      if (reward.claimedOn) {
        const claimDate = normalizeDate(reward.claimedOn);
        if (claimDate && claimDate.length > 0) {
          if (!historyItems[claimDate]) {
            historyItems[claimDate] = [];
            starsEarnedByDay[claimDate] = 0;
          }
          
          historyItems[claimDate].push({
            title: reward.title,
            stars: reward.cost,
            isTask: false
          });
        }
      }
    });
    
    // Create the history data array
    const historyData: DailyHistory[] = Object.keys(historyItems).map(date => ({
      date,
      starsEarned: starsEarnedByDay[date] || 0,
      items: historyItems[date]
    }));
    
    // Sort by date (newest first)
    historyData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    setHistory(historyData);
  }, [appData]);

  // Helper function to safely format dates
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error(`Invalid date: ${dateString}`);
        return dateString; // Return original string if can't parse
      }
      
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error(`Error formatting date ${dateString}:`, error);
      return dateString; // Return original string on error
    }
  };

  return (
    <div className="container max-w-4xl mx-auto p-4">
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/')}
          className="mr-2"
        >
          <ArrowLeft size={24} />
        </Button>
        <h1 className="text-3xl font-bold">Activity History</h1>
      </div>

      {history.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-lg text-muted-foreground">No activity history found.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Complete tasks and claim rewards to see your history here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {history.map((day) => (
            <Card key={day.date} className="overflow-hidden">
              <CardHeader className="bg-muted/50 p-4">
                <CardTitle className="flex justify-between items-center">
                  <span>
                    {formatDate(day.date)}
                  </span>
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    <span>{day.starsEarned} earned</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <ul className="space-y-2">
                  {day.items.map((item, index) => (
                    <li key={index} className="flex justify-between items-center">
                      <span>{item.title}</span>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>{item.stars} ⭐</span>
                        {item.isTask ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Award className="h-4 w-4 text-purple-500" />
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
